import React from 'react';
import { Box, Typography } from '@mui/material';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import LinkWithIconComponent from '../LinkWithIcon/LinkWithIconComponent';
import Image from '../../Image/Image';

const CARD_WIDTH = 389; // px
const CARD_HEIGHT = 306; // px
const GRID_GAP = 32; // px
const GRID_COLUMNS = 3;
const GRID_TOTAL_WIDTH =
  CARD_WIDTH * GRID_COLUMNS + GRID_GAP * (GRID_COLUMNS - 1); // 1231px

const IndicativeQuotesComponent = ({ data, color }) => {
  const {
    title,
    description,
    footerText,
    quotes = [],
    footerLinkDescription,
    footerLink,
  } = data || {};
  const hasSingleCardLastRow = quotes.length % GRID_COLUMNS === 1;
  const lastQuoteIndex = quotes.length - 1;

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: `min(100%, ${GRID_TOTAL_WIDTH + 64}px)`, // 64px = horizontal padding
          backgroundColor: color === 'grey' ? '#E3E6E8' : '#162F3B',
          borderRadius: '48px',
          pt: '32px',
          px: '32px',
          pb: '192px',

          boxSizing: 'border-box',
          textAlign: 'center',
        }}
      >
        <ScrollFadeWrapper variant="text">
          <Typography
            component="h3"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: '3.25rem',
              letterSpacing: '-0.03em',
              color: color === 'grey' ? '#162F3B' : '#FFFFFF',
              mb: '16px',
              whiteSpace: 'pre-line',
            }}
          >
            {title}
          </Typography>
        </ScrollFadeWrapper>

        <ScrollFadeWrapper variant="text" delay={0.2}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '1.5rem',
              lineHeight: '1.75rem',
              letterSpacing: '-0.03em',
              color: color === 'grey' ? '#162F3B' : '#FFFFFF',
              mb: '40px',
              whiteSpace: 'pre-line',
            }}
          >
            {description?.replace(
              '{CURRENT_MONTH_YEAR}',
              new Date().toLocaleString('en-GB', {
                month: 'long',
                year: 'numeric',
              })
            )}
          </Typography>
        </ScrollFadeWrapper>

        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${GRID_COLUMNS}, ${CARD_WIDTH}px)`,
              gridAutoRows: `${CARD_HEIGHT}px`,
              gap: `${GRID_GAP}px`,
              justifyContent: 'center',
              // This ensures the grid itself centers within the blue box
              width: `${GRID_TOTAL_WIDTH}px`,
              maxWidth: '100%',
            }}
          >
            {quotes.map((quote, index) => {
              const shouldCenterSingleLastCard =
                hasSingleCardLastRow && index === lastQuoteIndex;

              return (
                <ScrollFadeWrapper key={index} delay={0.4 + index * 0.1}>
                  <Box
                    sx={{
                      width: `${CARD_WIDTH}px`,
                      height: `${CARD_HEIGHT}px`,
                      backgroundColor: '#FFFFFF',
                      borderRadius: '64px',
                      pt: '24px',
                      boxShadow:
                        '0px 1px 2px 0px rgba(162, 172, 177, 0.2), 0px 12px 24px -4px rgba(22, 47, 59, 0.12)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      ...(shouldCenterSingleLastCard
                        ? { gridColumn: '2' }
                        : {}),
                    }}
                  >
                    <Image
                      width={222}
                      height={168}
                      src={quote.image}
                      alt={quote.imgAltTag || `Quote ${index + 1}`}
                      title={quote.imageAltTitle}
                      sx={{
                        width: '222px',
                        height: '168px',
                        mb: '32px',
                        mt: '24px',
                      }}
                    />
                    <Box
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        px: '32px',
                        pb: '64px',
                        width: '100%',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 400,
                          fontSize: '1.5rem',
                          lineHeight: '1.75rem',
                          letterSpacing: '-0.03em',
                          color: '#162F3B',
                          textAlign: 'center',
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {quote.text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
                          if (/^\*\*[^*]+\*\*$/.test(part)) {
                            return (
                              <strong key={i} style={{ fontWeight: 700 }}>
                                {part.slice(2, -2)}
                              </strong>
                            );
                          }
                          return part;
                        })}
                      </Typography>
                    </Box>
                  </Box>
                </ScrollFadeWrapper>
              );
            })}
          </Box>
        </Box>

        <Typography
          sx={{
            position: 'absolute',
            bottom: '74px',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: '1.5rem',
            letterSpacing: '-0.02em',
            color: color === 'grey' ? '#162F3B' : '#FFFFFF',
            maxWidth: '100%',
            whiteSpace: 'pre-line',
          }}
        >
          {footerText?.replace(
            '{CURRENT_MONTH_YEAR}',
            new Date().toLocaleString('en-GB', {
              month: 'long',
              year: 'numeric',
            })
          )}
        </Typography>

        <LinkWithIconComponent
          href={footerLink || undefined}
          iconSrc="https://assets.askhippo.co.za/Icons/common/Union.svg"
          iconSize="1rem"
          sx={{
            position: 'absolute',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: '1rem',
            fontWeight: 700,
            lineHeight: '1.5rem',
            letterSpacing: '-0.02em',
            color: color === 'grey' ? '#162F3B' : '#FFFFFF',
            maxWidth: '100%',
            whiteSpace: 'pre-line',
          }}
        >
          {footerLinkDescription}
        </LinkWithIconComponent>
      </Box>
    </ScrollFadeSection>
  );
};

export default IndicativeQuotesComponent;
